<template>
  <k-modal :show="showModal" @close="onClose" id="search-pics-modal">
    <template #header>
      <h3>Search PICS</h3>
    </template>
    <template #body>
      <p>The table below shows unlinked PICS episodes found automatically by matching to the learner's KATE email addresses.</p>
      <p>Use the email or name search to find additional episodes.</p>
      <div class="search-pics-container">
        <k-panel :hasContentToggle="false" class="search-pics-panel">
          <template #header>
            <h4 class="pics-sub-header"><i class="fa-regular fa-envelope" aria-hidden="true"></i>Email Search</h4>
          </template>
          <template #body>
            <div class="search-form-field">
              <label for="email-input">Email:</label>
              <div class="search-input-container">
                <input id="email-input" type="text" class="form-control ph-no-capture" placeholder="Enter email..." v-model="searchEmail">
                <button type="button" class="btn btn-primary" @click="searchByEmail" aria-describedby="search-by-email-description"><i class="fas fa-search" aria-hidden="true"></i> Search PICS</button>
                <span id="search-by-email-description" class="sr-only">Search by email</span>
              </div>
              <span v-if="searchedByEmail && episodesReady" class="new-episodes-count">{{ newEpisodesCount }} new episodes found</span>
            </div>
          </template>
        </k-panel>
        <k-panel :hasContentToggle="false" class="search-pics-panel">
          <template #header>
            <h4 class="pics-sub-header"><i class="fas fa-user" aria-hidden="true"></i>Name Search</h4>
          </template>
          <template #body>
            <div class="search-form-field">
              <label for="name-input">Name:</label>
              <div class="search-input-container">
                <input id="name-input" type="text" class="form-control" placeholder="Enter name..." v-model="searchName">
                <button type="button" class="btn btn-primary" @click="searchByName" aria-describedby="search-by-name-description"><i class="fas fa-search" aria-hidden="true"></i> Search PICS</button>
                <span id="search-by-name-description" class="sr-only">Search by name</span>
              </div>
              <span v-if="searchedByName && episodesReady" class="new-episodes-count">{{ newEpisodesCount }} new episodes found</span>
            </div>
          </template>
        </k-panel>
      </div>
      <k-panel :hasContentToggle="false">
        <template #header>
          <h4><i class="fas fa-link-slash" aria-hidden="true"></i>Unlinked PICS Episodes</h4>
        </template>
        <template #body>
          <p>Select the episodes matching the learner and click 'Add to Summary' to enable assignment.</p>
          <k-editable-table
            :headers="kableHeaders"
            v-model="tableRows"
            :editable="false"
          />
        </template>
      </k-panel>
    </template>
    <template #footer>
      <div class="modal-footer-left">
        <button class="btn btn-outlined" @click="onClose">Cancel</button>
      </div>
      <div class="modal-footer-right">
        <button class="btn btn-success" @click="confirmAdd" :disabled="confirmDisabled">Add to Summary</button>
      </div>
    </template>
  </k-modal>
</template>

<style>
  #search-pics-modal .modal-container {
    width: 80vw;
  }

  #search-pics-modal .k-editable-table-container {
    overflow: auto;
  }

</style>

<style scoped>
.search-input-container {
  display: flex;
  gap: 10px;
  align-items: center;
}

.pics-sub-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-pics-container {
  display: flex;
  gap: 20px;
}

.search-pics-panel {
  width: 50%;
}

.search-form-field {
  min-height: 90px;
}

.new-episodes-count {
  color: var(--kate-type-secondary);
}
</style>

<script>
import KEditableTable from '@base-components/k-editable-table.vue';
import KModal from '@base-components/k-modal.vue';
import TimeMixin from '@mixins/time-mixins';
import KPanel from '@base-components/k-panel.vue';
import PageReadyMixin from '@mixins/page-ready-mixin';

export default {

  components: {
    KModal,
    KEditableTable,
    KPanel,
  },

  mixins: [TimeMixin, PageReadyMixin],

  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    learnerId: {
      type: Number,
      required: true,
    },
    existingUnlinkedEpisodes: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      episodesReady: false,
      tableRows: [],
      searchEmail: undefined,
      searchName: undefined,
      newEpisodesCount: undefined,
      searchedByEmail: false,
      searchedByName: false,
      foundEpisodes: [],
    };
  },

  mounted() {
    this.getOrphanedPicsEpisodes();
  },

  computed: {
    kableHeaders() {
      return [
        {
          key: 'source',
          title: 'Source',
          type: 'pill',
          options: {
            type: source => ({
              auto: 'neutral',
              name: 'primary',
              email: 'proposed',
            }[source] || 'neutral'),
          },
        },
        {
          key: 'uln',
          title: 'ULN',
          type: 'string',
        },
        {
          key: 'full_name',
          title: 'Learner Name',
          type: 'string',
        },
        {
          key: 'email',
          title: 'Email',
          type: 'string',
        },
        {
          key: 'standard',
          title: 'Standard',
          type: 'string',
        },
        {
          key: 'status',
          title: 'Status',
          type: 'string',
        },
        {
          key: 'start_date',
          title: 'Start Date',
          type: 'string',
        },
        {
          key: 'end_date',
          title: 'End Date',
          type: 'string',
        },
        {
          key: 'checkbox',
          title: 'Select',
          editable: true,
          type: 'boolean',
          required: true,
          sortable: true,
        },
      ];
    },

    confirmDisabled() {
      return this.tableRows.filter(row => row.checkbox).length === 0;
    },
  },

  methods: {
    getOrphanedPicsEpisodes() {
      this.$Loading.start();
      this.episodesReady = false;
      this.$http.get(`/api/profile/ilr/user/${this.learnerId}/orphaned_episodes`, { params: { email: this.searchEmail, name: this.searchName } })
        .then(result => {
          const episodes = result.data.episodes || [];
          if (this.searchedByEmail || this.searchedByName) {
            this.mergeUniqueEpisodes(episodes);
          } else {
            const existingUnlinkedIds = new Set(this.existingUnlinkedEpisodes.map(episode => episode.pics_id));
            const uniqueEpisodes = episodes.filter(episode => !existingUnlinkedIds.has(episode.pics_id));
            this.foundEpisodes = uniqueEpisodes;
            this.tableRows = uniqueEpisodes.map(episode => this.formatEpisodeForTable(episode));
          }
          this.$logger.info('Got orphaned PICS episodes', { learnerId: this.learnerId });
        }).catch(error => {
          this.$logger.error('Error getting orphaned PICS episodes', { learnerId: this.learnerId }, error);
          this.$ktoast.showError(error);
        }).finally(() => {
          this.episodesReady = true;
          this.$Loading.finish();
        });
    },

    searchByEmail() {
      this.searchName = undefined;
      this.getOrphanedPicsEpisodes();
      this.searchedByEmail = true;
      this.searchedByName = false;
    },

    searchByName() {
      this.searchEmail = undefined;
      this.getOrphanedPicsEpisodes();
      this.searchedByName = true;
      this.searchedByEmail = false;
    },

    getSource() {
      if (this.searchedByEmail) {
        return 'email';
      }
      if (this.searchedByName) {
        return 'name';
      }
      return 'auto';
    },

    formatEpisodeForTable(episode) {
      return {
        source: this.getSource(),
        id: episode.pics_id,
        uln: episode.uln,
        full_name: episode.pics_full_name,
        standard: episode.ilr_standard,
        email: episode.pics_email,
        status: episode.ilr_status,
        start_date: this.formatDateString(episode.ilr_start_date),
        end_date: this.formatDateString(episode.ilr_planned_end_date),
        checkbox: false,
      };
    },

    mergeUniqueEpisodes(newEpisodes) {
      const foundEpisodeIds = new Set(this.tableRows.map(row => row.id));
      const existingUnlinkedIds = new Set(this.existingUnlinkedEpisodes.map(episode => episode.pics_id));
      const uniqueNewEpisodes = newEpisodes.filter(ep => !foundEpisodeIds.has(ep.pics_id) && !existingUnlinkedIds.has(ep.pics_id));
      this.foundEpisodes = [...this.foundEpisodes, ...uniqueNewEpisodes];
      const formattedEpisodes = uniqueNewEpisodes.map(episode => this.formatEpisodeForTable(episode));
      this.tableRows = [...this.tableRows, ...formattedEpisodes];
      this.newEpisodesCount = formattedEpisodes.length;
    },

    onClose() {
      this.$emit('close');
    },

    confirmAdd() {
      const selectedEpisodes = this.foundEpisodes
        .filter(episode => this.tableRows.find(row => row.id === episode.pics_id && row.checkbox));
      this.$emit('episodes-selected', selectedEpisodes);
      this.onClose();
    },
  },
};
</script>
